import { Components } from '.generated/templates/Feature.Components.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { classnames } from 'tailwindcss-classnames';
import EditingHelpText from 'components/helpers/EditingHelpText';
import HeadingTag from 'components/helpers/HeadingTag';
import RichTextA11yWrapper from 'components/helpers/RichTextA11yWrapper';
import ImageWrapper from 'components/helpers/ImageWrapper/ImageWrapper';
import ActionCardCta from './ActionCardCta';
import { getEnumValue } from 'lib/util/enum';
import { ValidHeadingLevels } from 'lib/sitecore/component-params';

export type ActionCardProps = Sitecore.Override.ComponentBase &
  Components.General.ActionCardCollection.Fields.ActionCard;

const ActionCard = ({ fields }: ActionCardProps): JSX.Element => {
  if (!fields) {
    return <EditingHelpText text="Action Card | No datasource set." />;
  }
  const { cta1, cta2, cta3, cta1Disable, cta2Disable, cta3Disable } = fields;
  const headingLevel = getEnumValue<ValidHeadingLevels>(fields.headingLevel, 'h3');
  const ctaProps = {
    leadingIcon: {
      default: '/-/media/project/nmdp/global/icons/nmdp-arrow-plum.png',
      hover: '/-/media/project/nmdp/global/icons/nmdp-arrow-plum-hover.png',
      active: '/-/media/project/nmdp/global/icons/nmdp-arrow-plum-active.png',
      disabled: '/-/media/project/nmdp/global/icons/nmdp-arrow-plum-disabled.png',
    },
    className: classnames(
      'inline-flex',
      'py-4',
      'items-center',
      'self-stretch',
      'text-theme-btn-link-text',
      'font-bold',
      'hover:underline',
      'active:underline',
      'aria-disabled:pointer-events-none',
      'aria-disabled:text-gray-midGray',
      'focus-visible:outline',
      'focus-visible:outline-2',
      'focus-visible:outline-[#0E5CC9]',
      'focus-visible:rounded'
    ),
  };

  return (
    <li
      data-component="authorable/general/actioncard"
      className={`${classnames(
        'shadow-callout-card',
        'flex',
        'h-auto',
        'flex-col',
        'flex-1',
        'lg:mb-0',
        'mb-5',
        'last:mb-0'
      )} lg:min-w-[30%] lg:max-w-[50%]`}
    >
      <div
        className={classnames(
          'rounded-t-lg',
          'aspect-w-16',
          'aspect-h-9',
          'relative',
          'w-full',
          'overflow-hidden'
        )}
      >
        <ImageWrapper field={fields.image} layout="fill" />
      </div>
      <div
        className={classnames(
          'rounded-b-lg',
          'bg-theme-action-card-bg',
          'flex',
          'flex-col',
          'flex-1'
        )}
      >
        <div
          className={classnames(
            'theme-white',
            'action-card-content',
            'pt-7',
            'px-6',
            'pb-6',
            'flex',
            'flex-col',
            'flex-1'
          )}
        >
          <HeadingTag
            headingLevel={headingLevel}
            className={classnames('heading-md', 'text-left', 'max-w-prose', 'mb-4')}
          >
            <Text field={fields.headingText} editable />
          </HeadingTag>
          <RichTextA11yWrapper
            className={classnames('p-0', 'rte')}
            field={fields.description}
            editable
          />
          <ul className={classnames('cta-container', 'mt-auto', 'pt-2', 'list-none')}>
            <li>
              <ActionCardCta field={cta1} disabled={cta1Disable?.value} {...ctaProps} />
            </li>
            <li>
              <ActionCardCta field={cta2} disabled={cta2Disable?.value} {...ctaProps} />
            </li>
            <li>
              <ActionCardCta field={cta3} disabled={cta3Disable?.value} {...ctaProps} />
            </li>
          </ul>
        </div>
      </div>
    </li>
  );
};

export default ActionCard;
